import { AccountLanguageCodeDTO } from '@app/shared/services/dtos/users/user-profile.dto';
import { Language } from '@app/shared/types/enums/language.type';

/** Account-level language the BE preference endpoints accept/return (PL/EN/NO only, never the 12-locale UI switcher). */
export type AccountLanguageCode = AccountLanguageCodeDTO;

/** `PL ↔ pl-PL`, `EN ↔ en-US`, `NO ↔ nb-NO` — no guessing from locale prefixes. */
export const ACCOUNT_LANGUAGE_TO_LOCALE: Readonly<Record<AccountLanguageCode, Language>> = {
  PL: Language.PL,
  EN: Language.EN,
  NO: Language.NB
};

/**
 * Validates and narrows a raw preference value to {@link AccountLanguageCode}.
 * Missing/null/unknown values are a contract error, never a silent default.
 */
export function parseAccountLanguage(value: unknown): AccountLanguageCode {
  if (value === 'PL' || value === 'EN' || value === 'NO') {
    return value;
  }
  throw new Error(`Unknown account language: ${String(value)}`);
}

export function accountLanguageToLocale(language: AccountLanguageCode): Language {
  return ACCOUNT_LANGUAGE_TO_LOCALE[language];
}

export interface UserProfileCompany {
  id: string;
  name: string;
}

/** Explicit allow-list merged into the full {@link User}; never the whole DTO cast to a user. */
export interface UserProfileMergeFields {
  firstName: string;
  lastName: string;
  email: string;
  address: string | null;
  phoneNumbers: string | null;
  notes: string | null;
}

/**
 * OwnUserProfile — small projection of `GET /api/v3/users/me/profile`.
 * Deliberately excludes status/dates/completion: those remain owned by the full {@link User} model.
 */
export class OwnUserProfile {
  public readonly id: string;
  public readonly companies: UserProfileCompany[];
  public readonly language: AccountLanguageCode;
  public readonly mergeFields: UserProfileMergeFields;

  public constructor(data: {
    id: string;
    companies: UserProfileCompany[];
    language: AccountLanguageCode;
    mergeFields: UserProfileMergeFields;
  }) {
    this.id = data.id;
    this.companies = data.companies;
    this.language = data.language;
    this.mergeFields = data.mergeFields;
  }
}
